import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { type Pricing } from '../domain/pricing';
import { type BillToClient, type ReservationClass, type TimeslotsPrices, type EChargingPrices, type ReservationClassProps, PricingMixEnum, type BillToEndUser, defaultReservationConfig } from '../domain/resaClass';

import { type Fleet } from '@/fleet/domain/fleet';
import { type Group } from '@/group/domain/group';
import { type GroupConfigEnterprise } from '@/group/domain/groupConfig';
import { type ParkCompPair } from '@/parking/domain/parking';

export interface pendingBillToClientSlice {
    biller: string;
    currency: string;
    pricingMix: string;
    flatFees: Pricing[];
    powerSupplyFees: Pricing[];
}
export interface pendingBillToEndUserSlice {
    biller: string;
    currency: string;
    eChargingPrices: EChargingPrices[] | null;
    timeslotsPrices: TimeslotsPrices;
}

export interface pendingGroupConfigEnterpriseSlice {
    pendingGroupConfigEnterprise: {
        __component: 'user.enterprise';
        name?: string;
        color?: string;
        logo?: string;
        memberSyncSource?: string;
        superAdminsGroup?: Group;
        superFleet?: Fleet;
        address?: {
            address: string;
            city: string;
            zip: string;
            country: string;
        };
        email?: string;
        phone?: string;
        contact?: {
            firstname: string;
            surname: string;
            email: string;
            phone: string;
            fax: string;
        }
    },
}

export interface SubscriptionState {
    pendingBillToClient?: pendingBillToClientSlice,
    billToClient?: BillToClient,
    pendingBillToEndUser?: pendingBillToEndUserSlice,
    billToEndUser?: BillToEndUser,
    companiesWithParkings?: ParkCompPair[],
    resaClasses?: ReservationClass[],
    currResaClass?: ReservationClass,
    client?: Group;
    pendingGroupConfigEnterprise?: GroupConfigEnterprise;
    pendingResaClass?: ReservationClassProps;
}

const initialState: SubscriptionState = {
    billToClient: {
        biller: '',
        currency: '',
        flatFees: [],
        powerSupplyFees: [],
        pricingMix: PricingMixEnum.flatFees,
    },
    billToEndUser: {
        biller: '',
        currency: '',
        eChargingPrices: [],
        timeslotsPrices: {
            biller: '',
            flatDays: [],
            hours: []
        },
    },
    client: {
        defaultDuration: 0,
        groupConfigs: [],
        id: -1,
        members: [],
        name: '',
        packet: {
            isOverflowFixed: false,
            resaClassRef: '',
            reservedNumber: 0,
        },
        prefix: '',
    },
    companiesWithParkings: [],

    pendingBillToClient: {
        biller: '',
        currency: '',
        flatFees: [],
        powerSupplyFees: [],
        pricingMix: '',
    },
    pendingBillToEndUser: {
        biller: '',
        currency: '',
        eChargingPrices: null,
        timeslotsPrices: {
            biller: '',
            flatDays: [],
            hours: []
        }
    },
    pendingGroupConfigEnterprise: {
        __component: 'user.enterprise',
        address: {
            address: '',
            city: '',
            country: '',
            zip: ''
        },
        contact: {
            email: '',
            fax: '',
            firstname: '',
            phone: '',
            surname: ''
        },
        id: -1,
        logo: '',
        name: '',
    },
    pendingResaClass: {
        config: { ...defaultReservationConfig },
        slotNumber: 0,
    },
    resaClasses: [],
};


const subscriptionSlice = createSlice({
    initialState: initialState,
    name: 'subscription',
    reducers: {
        clearBillToClient: (state) => {
            state.billToClient = initialState.billToClient;
        },
        clearCompaniesWithParkings: (state) => {
            state.companiesWithParkings = [];
        },
        clearPendingGroupConfigEnterprise: (state) => {
            state.pendingGroupConfigEnterprise = initialState.pendingGroupConfigEnterprise;
            return state;
        },
        clearPendingResaClass: (state) => {
            state.pendingResaClass = initialState.pendingResaClass;
        },
        clearCurrResaClass: (state) => {
            state.pendingResaClass = initialState.pendingResaClass;
        },
        clearResaClasses(state) {
            state.resaClasses = [];
        },
        clearSubscription() {
            return initialState;
        },
        clearpendingBillToClient: () => {
            return initialState;
        },
        setBillToClient(state, action: PayloadAction<BillToClient>) {
            state.billToClient = action.payload;
        },
        setBillToEndUser(state, action: PayloadAction<BillToEndUser>) {
            state.billToEndUser = action.payload;
        },
        setCompaniesWithParkings(state, action: PayloadAction<ParkCompPair[]>) {
            state.companiesWithParkings = action.payload;
        },
        setCurrResaClass: (
            state,
            action: PayloadAction<ReservationClass>
        ) => {
            return { ...state, currResaClass: action.payload, pendingResaClass: action.payload };
        },
        setPendingBillToClient: (state, action: PayloadAction<BillToClient>) => {
            state.pendingBillToClient = action.payload;
        },

        setPendingBillToEndUser: (state, action: PayloadAction<BillToEndUser>) => {
            state.pendingBillToEndUser = action.payload;
        },
        setPendingGroupConfigEnterprise: (state, action: PayloadAction<GroupConfigEnterprise>) => {
            state.pendingGroupConfigEnterprise = action.payload;
            return state;
        },
        setResaClasses(state, action: PayloadAction<ReservationClass[]>) {
            state.resaClasses = action.payload;
        },
        updateBillToEndUserProps: (state, action: PayloadAction<pendingBillToEndUserSlice>) =>
            // Make sure to return a new state object instead of mutating the existing one
            ({ ...state, ...action.payload }),
        updatePendingBillToClientProps: (state, action: PayloadAction<pendingBillToClientSlice>) =>
            // Make sure to return a new state object instead of mutating the existing one
            ({ ...state, ...action.payload }),

        updatePendingGroupConfigEnterpriseProps(state, action: PayloadAction<{ field: string, value: string }>) {
            const { field, value } = action.payload;

            // Check if superGroupConfig exists
            if (!state.pendingGroupConfigEnterprise) {
                return state; // Return current state if superGroupConfig doesn't exist
            }

            // Split field into nested parts
            const fieldParts = field.split('.');

            // If field is nested
            if (fieldParts.length > 1) {
                const topLevelField = fieldParts[0];
                const nestedFields = fieldParts.slice(1); // Remove the first part (top-level field)

                /*
                 * Check if top-level field exists 
                 * TODO : either set all possible fields in initialState or find another way to check ot topLevel field makes sense
                 */
                if (typeof (initialState.pendingGroupConfigEnterprise?.[topLevelField]) === 'undefined') {
                    return state; // Return current state if top-level field doesn't exist
                }

                // Create a new object with updated nested fields
                const updatedNestedFields = {
                    ...state.pendingGroupConfigEnterprise[topLevelField],
                    [nestedFields.join('.')]: value // Update the nested field
                };

                // Create a new superGroupConfig object with the updated nested fields
                const updatedPendingGroupConfigEnterprise = {
                    ...state.pendingGroupConfigEnterprise,
                    [topLevelField]: updatedNestedFields // Update the top-level field
                };

                // Return the new state with updated superGroupConfig
                state.pendingGroupConfigEnterprise = updatedPendingGroupConfigEnterprise;

                return state;
            } else {

                // If field is not nested, update it directly
                state.pendingGroupConfigEnterprise = {
                    ...state.pendingGroupConfigEnterprise,
                    [field]: value // Update the field
                };


                return state;
            }
        },
        updatePendingResaClassProps(state, action: PayloadAction<{ field: string, value: unknown }>) {
            const { field, value } = action.payload;

            // Check if pendingResaClass exists
            if (!state.pendingResaClass) {
                return state; // Return current state if pendingResaClass doesn't exist
            }

            // Split field into nested parts
            const fieldParts = field.split('.');

            // For nested paths, guard that the top-level key is known before writing.
            if (fieldParts.length > 1 && typeof (initialState.pendingResaClass[fieldParts[0]]) === 'undefined') {
                return state; // Return current state if top-level field doesn't exist
            }

            // Deep-set the (possibly multi-level, e.g. config.features.enableReservation)
            // path directly on the Immer draft so nested config sub-components are stored
            // as real nested objects.
            let target = state.pendingResaClass as Record<string, any>;
            for (let i = 0; i < fieldParts.length - 1; i++) {
                const key = fieldParts[i];
                if (target[key] == null || typeof target[key] !== 'object') {
                    target[key] = {};
                }
                target = target[key];
            }
            target[fieldParts.at(-1)!] = value;

            return state;
        },
    }
});

export const { clearPendingGroupConfigEnterprise,
    setPendingGroupConfigEnterprise, setCurrResaClass, clearCurrResaClass, clearPendingResaClass,
    updatePendingGroupConfigEnterpriseProps, clearSubscription, setPendingBillToClient, updatePendingBillToClientProps, clearpendingBillToClient, setPendingBillToEndUser, updateBillToEndUserProps,
    clearResaClasses, setResaClasses, clearCompaniesWithParkings, updatePendingResaClassProps, setCompaniesWithParkings, clearBillToClient, setBillToClient, setBillToEndUser } = subscriptionSlice.actions;

export default subscriptionSlice.reducer;